import { useEffect, useState } from "react";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";

/**
 * A debounced mirror of a value.
 *
 * Typing is not searching: the input renders the immediate value, and this hook
 * produces the value the *work* runs on — a filter pass, a URL write, a server
 * query. A burst of keystrokes therefore settles into one run once typing stops.
 *
 * Clearing is deliberately **not** debounced. Setting the value to `""` updates
 * the mirror immediately, because pressing the clear button and watching a list
 * stay filtered for another 300ms reads as a broken control rather than as a
 * debounce.
 *
 * @example
 * const [search, setSearch] = useState("");
 * const debouncedSearch = useDebouncedValue(search, 300);
 * <Input value={search} onChange={(e) => setSearch(e.target.value)} />
 * const filtered = useMemo(() => filter(items, debouncedSearch), [items, debouncedSearch]);
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  const { debounced: schedule, cancel } = useDebouncedCallback(setDebounced, delay);

  useEffect(() => {
    if (typeof value === "string" && value === "") {
      cancel();
      setDebounced(value);
      return;
    }
    schedule(value);
  }, [value, schedule, cancel]);

  return debounced;
}
